import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowUpRight, ChevronRight, Gauge, ShieldCheck } from "lucide-react";
import { Reveal } from "@/components/Motion";
import { DynoGraph } from "@/components/DynoGraph";
import { ToolCompatibility } from "@/components/ToolCompatibility";
import { STAGE_LABELS, VehicleDetailCtas, VehicleSpecChips, VehicleStageTable, vehicleYears } from "@/components/VehicleDetailSections";
import { API, SITE_ORIGIN, setJsonLd, removeJsonLd } from "@/lib/site";

export default function VehicleTuning() {
  const { make, model, generation, engine } = useParams();
  const [v, setV] = useState(null);
  const [miss, setMiss] = useState(false);

  useEffect(() => {
    setV(null);
    setMiss(false);
    axios
      .get(`${API}/tuning-database/page/${make}/${model}/${generation}/${engine}`)
      .then((r) => setV(r.data))
      .catch(() => setMiss(true));
  }, [make, model, generation, engine]);

  const years = useMemo(() => vehicleYears(v), [v]);
  const name = v ? `${v.make} ${v.model}` : "";
  const stages = useMemo(() => (v?.display_stages || []).filter((s) => STAGE_LABELS[s.key]), [v]);
  const s1 = stages.find((s) => s.key === "stage1");

  // ---- SEO head: unique title, meta description, canonical, structured data ----
  useEffect(() => {
    if (!v) return;
    const s1b = s1?.bhp ?? v.stage1_bhp;
    const s1n = s1?.nm ?? v.stage1_nm;
    const yearsTxt = years ? ` (${years})` : "";
    const titleTag = years || (v.generation || "").length <= 22 ? (years ? ` (${years})` : (v.generation ? ` (${v.generation})` : "")) : "";
    document.title = `${name} ${v.engine}${titleTag} Remap${s1b ? ` — ${v.original_bhp ?? "?"} to ${s1b} BHP` : ""} | Regal Tuning`;
    const descParts = [
      `${name} ${v.engine}${yearsTxt} tuning gains:`,
      v.original_bhp != null ? `factory ${v.original_bhp} BHP${v.original_nm != null ? ` / ${v.original_nm} Nm` : ""}` : null,
      s1b != null ? `Stage 1 takes it to ${s1b} BHP${s1n != null ? ` / ${s1n} Nm` : ""}` : null,
      v.gain_bhp != null ? `(+${v.gain_bhp} BHP${v.gain_nm != null ? `, +${v.gain_nm} Nm` : ""})` : null,
      "Verified ECU remapping from Regal Tuning — request your quote.",
    ].filter(Boolean);
    document.querySelector('meta[name="description"]')?.setAttribute("content", descParts.join(" ").slice(0, 300));
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.rel = "canonical";
      document.head.appendChild(canonical);
    }
    canonical.href = `${SITE_ORIGIN}${v.page_url}`;

    setJsonLd("vt-breadcrumb", {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_ORIGIN}/` },
        { "@type": "ListItem", position: 2, name: "Tuning Database", item: `${SITE_ORIGIN}/tuning-database` },
        { "@type": "ListItem", position: 3, name: `${name} ${v.engine}`, item: `${SITE_ORIGIN}${v.page_url}` },
      ],
    });
    setJsonLd("vt-vehicle", {
      "@context": "https://schema.org",
      "@type": "Vehicle",
      name: `${name} ${v.engine}${yearsTxt}`,
      brand: { "@type": "Brand", name: v.make },
      model: v.model,
      ...(years ? { vehicleModelDate: String(v.year_from || "") } : {}),
      ...(v.fuel ? { fuelType: v.fuel } : {}),
      vehicleEngine: {
        "@type": "EngineSpecification",
        name: v.engine,
        ...(v.engine_size ? { engineDisplacement: { "@type": "QuantitativeValue", value: v.engine_size.replace(/[^\d.]/g, ""), unitCode: "CMQ" } } : {}),
        ...(v.original_bhp != null ? { enginePower: { "@type": "QuantitativeValue", value: v.original_bhp, unitCode: "BHP" } } : {}),
        ...(v.original_nm != null ? { torque: { "@type": "QuantitativeValue", value: v.original_nm, unitCode: "NU" } } : {}),
      },
      url: `${SITE_ORIGIN}${v.page_url}`,
    });
    if (s1b != null) {
      const faqs = [
        [`How much power does a Stage 1 remap add to the ${name} ${v.engine}?`,
         `The ${name} ${v.engine} produces ${v.original_bhp ?? "its factory"} BHP${v.original_nm != null ? ` and ${v.original_nm} Nm` : ""} as standard. A Regal Tuning Stage 1 calibration takes it to ${s1b} BHP${s1n != null ? ` and ${s1n} Nm` : ""}${v.gain_bhp != null ? ` — a gain of ${v.gain_bhp} BHP${v.gain_nm != null ? ` and ${v.gain_nm} Nm` : ""}` : ""} with no hardware modifications required.`],
        [`Is a Stage 1 remap safe for the ${name} ${v.engine}?`,
         "Yes. Stage 1 calibrations are software-only and stay within the safe mechanical tolerances of the engine, gearbox and drivetrain. Performance data is compiled from Regal Tuning calibration records, established industry datasets and manufacturer specifications; estimated values are clearly identified."],
        [`How do I get my ${name} ${v.engine} remapped?`,
         "Request a quote with your vehicle details or registration and our calibration team will confirm the exact figures for your vehicle. Existing customers upload their ECU file through the Regal Tuning file portal."],
      ];
      setJsonLd("vt-faq", {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqs.map(([q, a]) => ({ "@type": "Question", name: q, acceptedAnswer: { "@type": "Answer", text: a } })),
      });
    }
    return () => {
      removeJsonLd("vt-breadcrumb");
      removeJsonLd("vt-vehicle");
      removeJsonLd("vt-faq");
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [v]);

  if (miss) {
    return (
      <main className="pt-32 sm:pt-40 pb-24" data-testid="vehicle-tuning-notfound">
        <div className="container-regal">
          <div className="border border-line bg-surface p-10 text-center">
            <Gauge size={28} className="mx-auto text-regal-amber" strokeWidth={1.5} />
            <h1 className="mt-3 font-display text-2xl font-extrabold uppercase tracking-tight text-ink">Vehicle not found</h1>
            <p className="mx-auto mt-2 max-w-md text-sm text-neutral-400">
              We couldn't find that exact vehicle. Search the full database or ask our team — coverage grows continuously.
            </p>
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              <Link to="/tuning-database" className="inline-flex items-center gap-2 bg-regal-amber px-6 py-3 font-display text-xs font-bold uppercase tracking-[0.14em] text-white hover:bg-amber-400 transition-colors" data-testid="vt-notfound-db-link">
                Search the Tuning Database
              </Link>
              <Link to="/contact" className="inline-flex items-center gap-2 border border-linebright px-6 py-3 font-display text-xs font-bold uppercase tracking-[0.14em] text-ink hover:border-regal-amber hover:text-regal-amber transition-colors" data-testid="vt-notfound-contact-link">
                Contact us
              </Link>
            </div>
          </div>
        </div>
      </main>
    );
  }

  if (!v) {
    return (
      <main className="pt-32 sm:pt-40 pb-24" data-testid="vehicle-tuning-loading">
        <div className="container-regal">
          <div className="h-96 animate-pulse border border-line bg-white" />
        </div>
      </main>
    );
  }

  const quoteUrl = `/contact?subject=${encodeURIComponent(`Remap quote — ${name} ${v.engine}${years ? ` (${years})` : ""}`)}&vehicle=${encodeURIComponent(`${name} ${v.engine}${years ? ` (${years})` : ""}`)}`;

  let faqGain = "Expected gains are confirmed in writing for your exact vehicle before work begins.";
  if (s1?.bhp != null) {
    const parts = [`Stage 1 takes the ${v.engine} to ${s1.bhp} BHP`];
    if (s1.nm != null) parts.push(`and ${s1.nm} Nm`);
    if (v.gain_bhp != null) {
      parts.push(`— a gain of ${v.gain_bhp} BHP`);
      if (v.gain_nm != null) parts.push(`and ${v.gain_nm} Nm`);
      parts.push("over stock");
    }
    faqGain = parts.join(" ") + ".";
  }

  return (
    <main className="pt-32 sm:pt-40 pb-24" data-testid="vehicle-tuning-page">
      <div className="container-regal">
        <nav className="mb-6 flex flex-wrap items-center gap-2 font-mono text-[11px] uppercase tracking-[0.16em] text-neutral-500" aria-label="Breadcrumb">
          <Link to="/" className="hover:text-regal-amber">Home</Link>
          <ChevronRight size={12} />
          <Link to="/tuning-database" className="hover:text-regal-amber" data-testid="vt-breadcrumb-db">Tuning Database</Link>
          <ChevronRight size={12} />
          <span className="text-neutral-300">{v.make}</span>
          <ChevronRight size={12} />
          <span className="text-neutral-300">{v.model} {v.engine}</span>
        </nav>

        <p className="overline-label mb-4">{v.vehicle_type} · ECU Remapping</p>
        <h1 className="max-w-4xl font-display text-3xl sm:text-5xl font-extrabold uppercase leading-[1.05] tracking-tight text-ink" data-testid="vt-h1">
          {name} <span className="text-regal-amber">{v.engine}</span> Tuning{years ? <span className="text-neutral-400"> ({years})</span> : null}
        </h1>
        <p className="mt-5 max-w-2xl text-sm sm:text-base text-neutral-400 leading-relaxed" data-testid="vt-intro">
          {`Factory specification and Stage 1 remap gains for the ${name} ${v.engine}${years ? ` (${years})` : ""}. `}
          {v.gain_bhp != null
            ? `A Stage 1 calibration adds ${v.gain_bhp} BHP${v.gain_nm != null ? ` and ${v.gain_nm} Nm` : ""} — software only, no hardware modifications required.`
            : "Stage 1 gains are confirmed in writing before any work begins."}
        </p>

        <VehicleSpecChips v={v} years={years} testPrefix="vt" />

        {/* PERFORMANCE TABLE */}
        <Reveal className="mt-10">
          <VehicleStageTable v={v} testPrefix="vt" />
        </Reveal>

        {/* DYNO GRAPH */}
        <Reveal>
          <DynoGraph vehicle={v} testPrefix="vt" />
        </Reveal>

        {/* TOOL COMPATIBILITY */}
        <Reveal>
          <ToolCompatibility items={v.tool_compat} testPrefix="vt" />
        </Reveal>

        {/* CTAs */}
        <VehicleDetailCtas v={v} quoteUrl={quoteUrl} testPrefix="vt" />

        {/* SEO CONTENT */}
        <div className="mt-16 grid gap-12 lg:grid-cols-2">
          <Reveal>
            <p className="overline-label mb-4">About this remap</p>
            <h2 className="font-display text-2xl sm:text-3xl font-bold uppercase tracking-tight text-ink" data-testid="vt-about-h2">
              {`${name} ${v.engine} Stage 1 — what changes`}
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-neutral-400">
              {`The ${name} ${v.engine}${years ? ` (${years})` : ""} leaves the factory at ${v.original_bhp ?? "its rated"} BHP${v.original_nm != null ? ` and ${v.original_nm} Nm of torque` : ""}. Manufacturers deliberately detune this engine to separate it from more expensive variants in the range — a Regal Tuning Stage 1 calibration safely recovers that headroom through the ECU alone: no hardware changes, no dyno risk, fully reversible.`}
            </p>
            <p className="mt-3 text-sm leading-relaxed text-neutral-400">
              {`Fuelling, boost pressure, ignition and torque limiters are optimised within the safe tolerances of the ${v.engine} powertrain${v.fuel ? ` (${v.fuel.toLowerCase()})` : ""}. The result is ${s1?.bhp ?? "a stronger"} BHP${s1?.nm != null ? ` and ${s1.nm} Nm` : ""} with factory smoothness, cold-start protection and emissions behaviour retained.`}
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="overline-label mb-4">Why Regal Tuning</p>
            <h2 className="font-display text-2xl sm:text-3xl font-bold uppercase tracking-tight text-ink">Backed by 20+ years of calibration work</h2>
            <p className="mt-4 text-sm leading-relaxed text-neutral-400">
              Performance data is compiled from Regal Tuning calibration records, established industry datasets and manufacturer specifications — estimated values are clearly identified. Your file is supplied through the Regal Tuning file portal with full technical support, and the original factory file is always kept so the vehicle can be returned to stock at any time.
            </p>
            <p className="mt-3 text-sm leading-relaxed text-neutral-400">
              Figures shown are for this exact engine variant. Gains depend on vehicle health, fuel quality and conditions — we confirm the expected output for your specific vehicle in writing before work begins.
            </p>
            <p className="mt-5">
              <Link to="/tuning-database" className="inline-flex items-center gap-2 font-display text-xs font-bold uppercase tracking-[0.16em] text-regal-amber hover:text-amber-400 transition-colors" data-testid="vt-back-to-db">
                Compare more vehicles in the Tuning Gains Database <ArrowUpRight size={13} />
              </Link>
            </p>
          </Reveal>
        </div>

        {/* FAQ */}
        {s1 && (
          <div className="mt-16" data-testid="vt-faq">
            <p className="overline-label mb-4">{`${name} ${v.engine} remap FAQ`}</p>
            <div className="grid gap-4 lg:grid-cols-3">
              {[
                ["How much power does Stage 1 add?", faqGain],
                ["Is Stage 1 safe for this engine?", "Yes — software-only calibration within the safe mechanical tolerances of the engine, gearbox and drivetrain. Figures are compiled from Regal Tuning calibration records, established industry datasets and manufacturer specifications; estimated values are clearly identified."],
                ["How do I get this remap?", "Request a quote with your vehicle details or registration. Existing customers upload their ECU file through the Regal Tuning file portal for a fast turnaround."],
              ].map(([q, a], i) => (
                <div key={q} className="h-full border border-line bg-white p-6" data-testid={`vt-faq-${i}`}>
                  <p className="font-display text-base font-bold uppercase tracking-wide text-ink">{q}</p>
                  <p className="mt-2.5 text-sm leading-relaxed text-neutral-400">{a}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* BOTTOM CTA */}
        <div className="mt-16 flex flex-col items-start justify-between gap-6 border border-line bg-surface p-8 sm:flex-row sm:items-center" data-testid="vt-bottom-cta">
          <div>
            <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-regal-amber"><ShieldCheck size={14} /> 20+ years of ECU calibration</p>
            <h2 className="mt-2 font-display text-xl sm:text-2xl font-bold uppercase tracking-tight text-ink">{`Ready to unlock your ${name}'s real performance?`}</h2>
            <p className="mt-2 max-w-xl text-sm text-neutral-400">Tell us your vehicle and registration — we'll confirm support and the exact expected gains for your engine before any work begins.</p>
          </div>
          <Link to={quoteUrl} className="inline-flex shrink-0 items-center gap-2 bg-regal-amber px-8 py-4 font-display text-sm font-bold uppercase tracking-[0.14em] text-white hover:bg-amber-400 transition-colors" data-testid="vt-bottom-cta-button">
            Request a Quote <ArrowUpRight size={15} />
          </Link>
        </div>
      </div>
    </main>
  );
}
